<template>
  <section
    ref="container"
    class="former-form"
  >
    <former-error
      v-if="currentErrors"
      key="former-error"
      :details="errorDetails"
    />

    <div class="former-header">
      <slot name="header" />
    </div>

    <former-container>
      <former-section-list
        v-for="section in visibleSections"
        :key="section.id"
        :desktop="desktop"
        :editable="editable"
        :section="section"
        @edit="emitEdit"
        @input="emitInput"
        @change="change"
      />
    </former-container>

    <div
      ref="composer"
      :class="composerClasses"
    >
      <former-field
        v-if="!desktop && visibleField"
        ref="field"
        class=""
        :field="visibleField"
        @error="emitError"
        @change="change"
        @resize="bottom"
      />

      <div
        v-else-if="editable"
        class="former-form-button-container"
      >
        <slot name="footer" />

        <former-button
          class="former-form-button"
          :icon="button.icon"
          :loading="loading"
          :size="button.size"
          :color="button.color"
          padded
          @click="submit"
        >
          {{ button.label }}
        </former-button>
      </div>
    </div>
  </section>
</template>

<script>
  import validators from '@cordel/helpers/form/validators/_index';

  import findItem from '@/_CRUDL/helpers/form/find';

  import scrollHelper from '@/helpers/views/scroll';

  import FormerButton from '@/components/Former/Button.vue';
  import FormerContainer from '@/components/Former/Container.vue';
  import FormerError from '@/components/Former/Error.vue';
  import FormerField from '@/components/Former/Field.vue';
  import FormerSectionList from '@/components/Former/Section/List.vue';

  // item helpers
  const isNotFilled = i => !i.separator && [null, undefined].indexOf(i.value) > -1;

  // section helpers
  const isPushable = s => s.items.filter(i => !i.separator && !i.skip).length;

  export default {
    name: 'FormerForm',
    components: {
      FormerButton,
      FormerContainer,
      FormerError,
      FormerField,
      FormerSectionList,
    },
    props: {
      editable: {
        type: Boolean,
        default: true,
      },
      sections: {
        type: Array,
        default() {
          return [];
        },
      },
      button: {
        type: Object,
        default() {
          return {
            skip: false,
            label: 'Salvar',
            icon: '',
          };
        },
      },
      loading: {
        type: Boolean,
        default: false,
      },
      error: {
        type: [Error, Object],
        default() {
          return {};
        },
      },
      errorDetails: {
        type: String,
        default() {
          return '';
        },
      },
    },
    data() {
      return {
        height: window.innerHeight,
        width: window.innerWidth,

        invalid: false,
        editing: true,
      };
    },
    computed: {
      desktop() {
        return true;
      },

      classes() {
        const classes = ['former-form'];

        return classes;
      },

      currentErrors() {
        return this.invalid || !!Object.keys(this.error || {}).length;
      },

      visibleEmpty() {
        return !!this.visibleSections[0].items.length;
      },
      visibleField() {
        const item = (i) => {
          if (i.skip || i.separator || i.type === 'file') {
            return false;
          }

          return i.editing || i.processing || [null, undefined].indexOf(i.value) > -1;
        };

        return findItem(this.sections, item);
      },
      visibleSections() {
        if (this.desktop) {
          return this.sections
            .filter(section => isPushable(section))
            .map((section) => {
              const items = section.items.filter(i => !i.skip);

              while (items.length && items[items.length - 1].separator) {
                items.pop();
              }

              return {
                ...section,
                items,
              };
          });
        }

        const active = this.visibleField || {};
        const sections = [];

        this.sections.some((section) => {
          const items = [];

          section.items.some((item) => {
            if (item.id === active.id) {
              return true;
            }

            if (item.skip) {
              return false;
            }

            return items.push(item) && isNotFilled(item);
          });

          if (isPushable(section)) {
            sections.push({
              ...section,
              items,
            });
          }

          return section.items
            .filter(i => i.id === active.id || (!i.skip && !i.type === 'file' && isNotFilled(i)))
            .length;
        });

        return sections;
      },

      composerClasses() {
        const classes = ['former-form-composer'];

        if (this.desktop || !this.visibleField) {
          classes.push('former-form-composer-transparent');
        }

        return classes;
      },
    },
    mounted() {
      window.addEventListener('resize', this.layout);
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.layout);
    },
    methods: {
      bottom() {
        if (this.desktop) {
          return;
        }

        this.$nextTick(this.scroll);
        setTimeout(this.scroll, 200);
      },
      scroll() {
        scrollHelper(99999, false);
      },
      layout() {
        this.height = window.innerHeight;
        this.width = window.innerWidth;
      },
      change(id, value = '') {
        const errors = this.validate(id, value);

        if (this.invalid) {
          this.invalid = false;
        }

        if (!errors.length || this.desktop) {
          this.emitChange(id, value);
        }

        if (errors.length) {
          this.emitError(id, errors);
        }
      },
      validate(id, value) {
        const errors = [];

        const {
          validations,
        } = findItem(this.sections, i => i.id === id);

        if (!validations) {
          return [];
        }

        validations.forEach((validation) => {
          const invalidation = validators[validation.type];

          if (invalidation) {
            if (invalidation(value, validation)) {
              errors.push(invalidation.message(value, validation));
            }
          }
        });

        return errors;
      },
      submit() {
        let valid = true;

        this.sections.forEach(section => section.items
          .filter(i => !i.skip)
          .forEach((item) => {
            const errors = this.validate(item.id, item.value);

            if (errors.length) {
              valid = false;

              this.emitError(item.id, errors);
            }
          }));

        this.invalid = !valid;

        if (valid) {
          this.emitSubmit();
        } else {
          scrollHelper();
        }
      },
      emitSubmit() {
        this.$emit('submit');
      },
      emitError(id, errors) {
        this.$emit('error', id, errors);

        this.bottom();
      },
      emitEdit(id) {
        this.editing = true;

        this.$emit('edit', id);
      },
      emitChange(id, value) {
        this.$emit('change', id, value);

        if (this.editing) {
          this.editing = false;

          return;
        }

        this.bottom();
      },
      emitInput(id, value) {
        this.$emit('change', id, value);
      },
    },
  };
</script>

<style lang="scss">
  .former-form > .former-container > .former-box:first-child {
    padding-top: 0;
  }
</style>

<style lang="scss" scoped>
  @import '@/assets/stylesheets/_variables.scss';

  .former-form {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .former-form-composer {
    background-color: $gray-light;
    border-top: $spacing solid $gray-light;
    float: left;
    margin-bottom: calc(env(safe-area-inset-bottom) * -1);
    padding-bottom: env(safe-area-inset-bottom);
    transition: background-color 0.1s;
  }

  .former-form-composer-transparent {
    background-color: transparent;
  }

  .former-form-button-container {
    align-items: center;
    display: flex;
    flex-direction: column-reverse;
    justify-content: center;
    margin: $gutter-big var(--product--pwa--horizontal) 0;

    @media all and (min-width: $breakpoint-desktop) {
      flex-direction: row;
      justify-content: flex-end;
      margin: $gutter-big var(--product--pwa--horizontal) 0;
    }
  }

  .former-form-button {
    width: 100%;

    @media all and (min-width: $breakpoint-desktop) {
      width: auto;
    }
  }

  .former-header {
    flex: 1;
  }
</style>
